import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  infoGrid: {
    marginBottom: theme.spacing(2),
  },
  infoLabel: {
    fontWeight: 'bold',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  headerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}));

const CommissionDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCommission();
  }, [id]);

  const fetchCommission = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const commissionDoc = doc(firestore, 'commissions', id);
      const commissionSnapshot = await getDoc(commissionDoc);
      
      if (commissionSnapshot.exists()) {
        setCommission({
          id: commissionSnapshot.id,
          ...commissionSnapshot.data()
        });
      } else {
        // Si la commission n'existe pas, utiliser des données de démonstration
        if (id === 'COM001') {
          setCommission({ 
            id: 'COM001', 
            partnerId: 'P001',
            partnerName: 'Entreprise ABC',
            tripId: 'T001',
            tripReference: 'VOY-2025-001',
            tripDetails: {
              origin: 'Paris',
              destination: 'Lyon',
              departureDate: '2025-03-15T08:00:00'
            },
            bookingId: 'B001',
            bookingReference: 'RES-2025-001',
            bookingDetails: {
              userName: 'Alice Martin',
              passengerCount: 2,
              totalAmount: 120.50
            },
            amount: 45.50,
            currency: 'EUR',
            status: 'pending',
            description: 'Commission pour trajet Paris-Lyon',
            createdAt: '2025-03-15T10:30:00',
            updatedAt: '2025-03-15T10:30:00',
            paymentDate: null,
            paymentReference: null,
            paymentMethod: null,
            notes: 'Commission standard pour partenaire régulier'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Commission non trouvée',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching commission:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de la commission',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/commissions');
  };

  const handleEdit = () => {
    navigate(`/admin/commissions/${id}/edit`);
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'commissions', id));
      
      setSnackbar({
        open: true,
        message: 'Commission supprimée avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste des commissions après un court délai
      setTimeout(() => {
        navigate('/admin/commissions');
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting commission:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de la commission',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const handleExportPDF = () => {
    // Logique d'exportation PDF
    // Dans une implémentation réelle, cela générerait un fichier PDF
    setSnackbar({
      open: true,
      message: 'Export PDF en cours...',
      severity: 'info'
    });
    
    // Simulation d'un délai pour l'export
    setTimeout(() => {
      setSnackbar({
        open: true,
        message: 'Commission exportée en PDF avec succès',
        severity: 'success'
      });
    }, 1500);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'primary';
      case 'paid':
        return 'default';
      case 'cancelled':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'paid':
        return 'Payée';
      case 'cancelled':
        return 'Annulée';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : commission ? (
          <>
            <div className={classes.headerActions}>
              <Typography variant="h4" component="h1">
                Détails de la commission
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={handleExportPDF}
              >
                Exporter PDF
              </Button>
            </div>
            
            <Box className={classes.section}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" component="h2">
                    Commission {commission.id}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {commission.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} style={{ textAlign: 'right' }}>
                  <Chip 
                    label={getStatusLabel(commission.status)} 
                    color={getStatusColor(commission.status)} 
                    className={classes.chip}
                  />
                  <Typography variant="h6" style={{ marginTop: 8 }}>
                    {formatCurrency(commission.amount, commission.currency)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            <Divider className={classes.divider} />
            
            <Box className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Informations générales
              </Typography>
              
              <Grid container spacing={2} className={classes.infoGrid}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Partenaire
                  </Typography>
                  <Typography variant="body1">
                    {commission.partnerName} ({commission.partnerId})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Date de création
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(commission.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Dernière modification
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(commission.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            <Divider className={classes.divider} />
            
            <Box className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Détails du voyage
              </Typography>
              
              <Grid container spacing={2} className={classes.infoGrid}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Référence voyage
                  </Typography>
                  <Typography variant="body1">
                    {commission.tripReference}
                  </Typography>
                </Grid>
                {commission.tripDetails && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Trajet
                      </Typography>
                      <Typography variant="body1">
                        {commission.tripDetails.origin} → {commission.tripDetails.destination}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Date de départ
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(commission.tripDetails.departureDate)}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
              
              <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
                Détails de la réservation
              </Typography>
              
              <Grid container spacing={2} className={classes.infoGrid}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Référence réservation
                  </Typography>
                  <Typography variant="body1">
                    {commission.bookingReference}
                  </Typography>
                </Grid>
                {commission.bookingDetails && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Client
                      </Typography>
                      <Typography variant="body1">
                        {commission.bookingDetails.userName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Montant total de la réservation
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(commission.bookingDetails.totalAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Nombre de passagers
                      </Typography>
                      <Typography variant="body1">
                        {commission.bookingDetails.passengerCount}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
            
            <Divider className={classes.divider} />
            
            <Box className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Détails financiers
              </Typography>
              
              <Grid container spacing={2} className={classes.infoGrid}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Montant de la commission
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(commission.amount, commission.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Date de paiement
                  </Typography>
                  <Typography variant="body1">
                    {commission.paymentDate ? formatDate(commission.paymentDate) : 'Non payée'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Référence de paiement
                  </Typography>
                  <Typography variant="body1">
                    {commission.paymentReference || '-'}
                  </Typography>
                </Grid>
              </Grid>
              
              {commission.notes && (
                <Grid container spacing={2} className={classes.infoGrid}>
                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.infoLabel}>
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {commission.notes}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
            
            <Divider className={classes.divider} />
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBack}
                className={classes.button}
              >
                Retour
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                className={classes.button}
                disabled={commission.status === 'paid'}
              >
                Modifier
              </Button>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                className={`${classes.button} ${classes.deleteButton}`}
                disabled={commission.status === 'paid'}
              >
                Supprimer
              </Button>
            </div>
          </>
        ) : (
          <Typography variant="body1">
            Commission non trouvée
          </Typography>
        )}
      </Paper>
      
      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer définitivement la commission <strong>{commission?.id}</strong> ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionDetailPage;
